// Map filter bar — renders chips, search, and saved-view dropdown.
//
// Mounted in a host div above the surface canvas when the active tab is
// cluster / matrix / cloud. Calls back with a fresh MapFilters object on any
// change. The caller owns persistence + re-rendering downstream visuals.

import {
  LENSES,
  LENS_LABEL,
  MACROS,
  MACRO_LABEL,
  type Lens,
  type Macro,
} from "../lib/macros.ts";
import {
  cloneFilters,
  deleteView,
  filtersToHashSuffix,
  isEmpty,
  listSavedViews,
  saveView,
  type MapFilters,
} from "../lib/filters.ts";

const escHtml = (s: string) =>
  s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!),
  );

interface RenderOpts {
  /** Active tab; used to write the filter into the URL hash. */
  tab: "cluster" | "matrix" | "cloud";
  /** Total topics matched (rendered alongside the controls). */
  matchedCount: number;
  totalCount: number;
}

export function renderMapFilters(
  host: HTMLElement,
  filters: MapFilters,
  onChange: (next: MapFilters) => void,
  opts: RenderOpts,
): () => void {
  const macroChips = MACROS
    .map((m) => `
      <button class="chip-toggle ${filters.macros.has(m) ? "on" : ""}" data-macro="${m}">
        ${MACRO_LABEL[m]}
      </button>
    `)
    .join("");

  const lensChips = LENSES
    .map((l) => `
      <button class="chip-toggle ${filters.lenses.has(l) ? "on" : ""}" data-lens="${l}">
        ${LENS_LABEL[l]}
      </button>
    `)
    .join("");

  const views = listSavedViews();
  const viewItems = views.map((v) => `
    <li class="saved-view-item">
      <button class="saved-view-load" data-view-id="${v.id}">${escHtml(v.name)}</button>
      <button class="saved-view-del" data-view-id="${v.id}" title="Delete view">×</button>
    </li>
  `).join("");

  host.innerHTML = `
    <div class="map-filters">
      <div class="map-filters-row">
        <div class="map-search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.5-4.5"/></svg>
          <input id="filterSearch" type="search" placeholder="Search topics, terms, aliases…" value="${escHtml(filters.search)}" />
        </div>
        <div class="map-count">
          <span class="map-count-num">${opts.matchedCount}</span>
          <span class="map-count-of">/ ${opts.totalCount}</span>
        </div>
      </div>
      <div class="map-filters-row">
        <span class="map-filters-label">Outside</span>
        <div class="chip-group" id="macroChips">${macroChips}</div>
        <span class="map-filters-label">Inside</span>
        <div class="chip-group" id="lensChips">${lensChips}</div>
        <button class="chip-toggle ${filters.h3Only ? "on" : ""}" id="toggleH3">H3 only</button>
        <button class="chip-toggle ${filters.movedOnly ? "on" : ""}" id="toggleMoved">Curated</button>
        ${isEmpty(filters) ? "" : `<button class="chip-clear" id="clearFilters">Clear</button>`}
        <span class="map-filters-spacer"></span>
        <div class="saved-views" id="savedViews">
          <button class="saved-views-toggle" id="savedViewsToggle">
            Views ${views.length ? `<span class="saved-views-count">${views.length}</span>` : ""}
          </button>
          <div class="saved-views-menu hidden" id="savedViewsMenu">
            <ul class="saved-view-list">
              ${viewItems || `<li class="saved-view-empty muted">No saved views yet.</li>`}
            </ul>
            <button class="saved-views-save" id="saveCurrentView" ${isEmpty(filters) ? "disabled" : ""}>
              Save current as…
            </button>
          </div>
        </div>
      </div>
    </div>
  `;

  // --- Helpers ---
  const emit = (next: MapFilters) => {
    // Reflect to hash so cockpit drill-ins and reload survive the active filter.
    const tabPrefix = `#/${opts.tab}`;
    const suffix = filtersToHashSuffix(next);
    if (location.hash !== tabPrefix + suffix) {
      // History-replace so we don't pollute the back stack on every keystroke.
      history.replaceState(null, "", tabPrefix + suffix);
    }
    onChange(next);
  };

  // --- Search ---
  const search = host.querySelector<HTMLInputElement>("#filterSearch")!;
  let debounce: number | undefined;
  search.addEventListener("input", () => {
    window.clearTimeout(debounce);
    debounce = window.setTimeout(() => {
      const next = cloneFilters(filters);
      next.search = search.value;
      emit(next);
    }, 200);
  });

  // --- Macro chips ---
  host.querySelectorAll<HTMLButtonElement>("#macroChips .chip-toggle").forEach((btn) => {
    btn.addEventListener("click", () => {
      const m = btn.dataset.macro as Macro;
      const next = cloneFilters(filters);
      if (next.macros.has(m)) next.macros.delete(m);
      else next.macros.add(m);
      emit(next);
    });
  });

  // --- Lens chips ---
  host.querySelectorAll<HTMLButtonElement>("#lensChips .chip-toggle").forEach((btn) => {
    btn.addEventListener("click", () => {
      const l = btn.dataset.lens as Lens;
      const next = cloneFilters(filters);
      if (next.lenses.has(l)) next.lenses.delete(l);
      else next.lenses.add(l);
      emit(next);
    });
  });

  // --- Toggles ---
  host.querySelector<HTMLButtonElement>("#toggleH3")?.addEventListener("click", () => {
    const next = cloneFilters(filters);
    next.h3Only = !next.h3Only;
    emit(next);
  });
  host.querySelector<HTMLButtonElement>("#toggleMoved")?.addEventListener("click", () => {
    const next = cloneFilters(filters);
    next.movedOnly = !next.movedOnly;
    emit(next);
  });

  // --- Clear ---
  host.querySelector<HTMLButtonElement>("#clearFilters")?.addEventListener("click", () => {
    emit({ search: "", macros: new Set(), lenses: new Set(), h3Only: false, movedOnly: false });
  });

  // --- Saved views menu ---
  const menu = host.querySelector<HTMLElement>("#savedViewsMenu");
  host.querySelector<HTMLButtonElement>("#savedViewsToggle")?.addEventListener("click", (e) => {
    e.stopPropagation();
    menu?.classList.toggle("hidden");
  });
  document.addEventListener("click", (e) => {
    if (!host.contains(e.target as Node)) menu?.classList.add("hidden");
  }, { once: true });

  host.querySelectorAll<HTMLButtonElement>(".saved-view-load").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.dataset.viewId!;
      const v = listSavedViews().find((sv) => sv.id === id);
      if (v) emit(cloneFilters(v.filters));
      menu?.classList.add("hidden");
    });
  });
  host.querySelectorAll<HTMLButtonElement>(".saved-view-del").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const id = btn.dataset.viewId!;
      deleteView(id);
      // Re-render to reflect the shorter list.
      renderMapFilters(host, filters, onChange, opts);
    });
  });
  host.querySelector<HTMLButtonElement>("#saveCurrentView")?.addEventListener("click", () => {
    if (isEmpty(filters)) return;
    const name = prompt("Name this view:");
    if (!name?.trim()) return;
    saveView(name.trim(), filters);
    renderMapFilters(host, filters, onChange, opts);
  });

  return () => {
    host.innerHTML = "";
  };
}
